import { useRef, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Download, Eye, FileText, ImageIcon, Paperclip, Trash2, Upload } from 'lucide-react';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { TicketAttachmentPreview } from './TicketAttachmentPreview';
import {
  useDeleteTicketAttachment,
  useTicketAttachments,
  useUploadTicketAttachment,
} from '../../hooks/queries/useTickets';
import type { TicketAttachment } from '../../services/tickets/ticketService';

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/svg+xml',
];
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType === 'application/pdf') {
    return <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />;
  }
  if (
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />;
  }
  if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />;
  }
  if (mimeType.startsWith('image/')) {
    return <ImageIcon className="w-5 h-5 text-purple-500 flex-shrink-0" />;
  }
  return <FileText className="w-5 h-5 text-neutral-400 flex-shrink-0" />;
}

interface AttachmentRowProps {
  attachment: TicketAttachment;
  ticketId: string;
  canDelete: boolean;
  onPreview: (attachment: TicketAttachment) => void;
}

function AttachmentRow({ attachment, ticketId, canDelete, onPreview }: AttachmentRowProps) {
  const deleteAttachment = useDeleteTicketAttachment();

  function handleDownload() {
    if (attachment.signed_url) {
      window.open(attachment.signed_url, '_blank', 'noopener,noreferrer');
    }
  }

  function handleDelete() {
    if (!window.confirm(`Supprimer « ${attachment.filename} » ?`)) return;
    deleteAttachment.mutate({
      attachmentId: attachment.id,
      storagePath: attachment.storage_path,
      ticketId,
    });
  }

  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg border border-neutral-100 bg-neutral-50 hover:bg-neutral-100 transition-colors group">
      <FileIcon mimeType={attachment.mime_type} />

      <button
        type="button"
        onClick={() => onPreview(attachment)}
        className="flex-1 min-w-0 text-left"
        title="Prévisualiser"
      >
        <p className="text-sm font-medium text-neutral-800 truncate hover:text-cime-primary transition-colors">
          {attachment.filename}
        </p>
        <p className="text-xs text-neutral-400">
          {formatBytes(attachment.size_bytes)}
          {' · '}
          {format(new Date(attachment.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
          {attachment.uploader && ` · ${attachment.uploader.full_name}`}
        </p>
      </button>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onPreview(attachment)}
          title="Prévisualiser"
          className="p-1.5 rounded text-neutral-500 hover:text-cime-primary hover:bg-white transition-colors"
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={handleDownload}
          disabled={!attachment.signed_url}
          title="Télécharger"
          className="p-1.5 rounded text-neutral-500 hover:text-cime-primary hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
        </button>
        {canDelete && (
          <button
            onClick={handleDelete}
            disabled={deleteAttachment.isPending}
            title="Supprimer"
            className="p-1.5 rounded text-neutral-500 hover:text-red-600 hover:bg-white transition-colors disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

interface TicketAttachmentsProps {
  ticketId: string;
  canUpload: boolean;
  canDelete: boolean;
}

export function TicketAttachments({ ticketId, canUpload, canDelete }: TicketAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<TicketAttachment | null>(null);
  const { data: attachments, isLoading } = useTicketAttachments(ticketId);
  const upload = useUploadTicketAttachment();

  function processFile(file: File) {
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      alert(`Type de fichier non autorisé : ${file.type || file.name}`);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      alert(`Le fichier dépasse la taille maximale autorisée (10 Mo).`);
      return;
    }
    upload.mutate({ ticketId, file });
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* File list */}
      {attachments && attachments.length > 0 ? (
        <div className="space-y-2">
          {attachments.map((att) => (
            <AttachmentRow
              key={att.id}
              attachment={att}
              ticketId={ticketId}
              canDelete={canDelete}
              onPreview={setPreviewAttachment}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-400 italic">Aucune pièce jointe.</p>
      )}

      {/* Upload zone */}
      {canUpload && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-cime px-4 py-4 text-center transition-colors ${
            dragOver
              ? 'border-cime-primary bg-cime-primary/5'
              : 'border-neutral-200 hover:border-neutral-300'
          }`}
        >
          <Upload className="w-5 h-5 text-neutral-400 mx-auto mb-2" />
          <p className="text-xs text-neutral-500 mb-2">
            Glissez un fichier ici ou
          </p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending}
            isLoading={upload.isPending}
          >
            <Paperclip className="w-3.5 h-3.5" />
            Joindre un fichier
          </Button>
          <p className="text-xs text-neutral-400 mt-2">
            PDF, Word, Excel, image — 10 Mo max
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_MIME_TYPES.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}

      <TicketAttachmentPreview
        attachment={previewAttachment}
        onOpenChange={(open) => { if (!open) setPreviewAttachment(null); }}
      />
    </div>
  );
}
